import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Field, Text, Textarea, Toggle } from "@/admin/components/AdminField";
import ImageUpload from "@/admin/components/ImageUpload";

const EMPTY = {
  slug: "", name: "", city: "", state: "", date: "", time: "",
  short_description: "", description: "",
  cover_image: "", registration_url: "",
  featured: false, published: true,
  seo: { meta_title: "", meta_description: "", og_image: "" },
};

export default function EventForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      adminApi.get(`/admin/events/${id}`).then((r) => setF({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => toast.error("Falha ao carregar")).finally(() => setLoading(false));
    }
  }, [id, editing]);

  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const setSeo = (patch) => setF((s) => ({ ...s, seo: { ...s.seo, ...patch } }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await adminApi.put(`/admin/events/${id}`, f);
      else await adminApi.post(`/admin/events`, f);
      toast.success(editing ? "Evento atualizado" : "Evento criado");
      navigate("/admin/eventos");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao salvar");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-[#B8B8B8]"><Loader2 className="inline animate-spin" size={16} /> Carregando…</div>;

  return (
    <form onSubmit={submit} className="space-y-8 max-w-4xl" data-testid="event-form">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/admin/eventos" className="text-[10px] tracking-[0.25em] uppercase text-[#B8B8B8] inline-flex items-center gap-2 hover:text-[#C6A762]">
            <ArrowLeft size={12} /> Voltar
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl mt-3">{editing ? "Editar evento" : "Novo evento"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <Toggle value={f.featured} onChange={(v) => set({ featured: v })} label="Em destaque" />
          <Toggle value={f.published} onChange={(v) => set({ published: v })} label="Publicado" />
          <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2 ml-auto sm:ml-0">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
          </button>
        </div>
      </div>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Nome"><Text value={f.name} onChange={(v) => set({ name: v, slug: f.slug || v })} required /></Field>
          <Field label="Slug"><Text value={f.slug} onChange={(v) => set({ slug: v })} /></Field>
          <Field label="Cidade"><Text value={f.city} onChange={(v) => set({ city: v })} required /></Field>
          <Field label="Estado"><Text value={f.state} onChange={(v) => set({ state: v })} placeholder="MG" required /></Field>
          <Field label="Data"><Text type="date" value={f.date} onChange={(v) => set({ date: v })} required /></Field>
          <Field label="Horário"><Text type="time" value={f.time} onChange={(v) => set({ time: v })} /></Field>
          <Field label="Link de inscrição" cols={2}><Text value={f.registration_url} onChange={(v) => set({ registration_url: v })} placeholder="https://…" /></Field>
        </div>
        <Field label="Descrição curta"><Textarea rows={2} value={f.short_description} onChange={(v) => set({ short_description: v })} /></Field>
        <Field label="Descrição completa"><Textarea rows={5} value={f.description} onChange={(v) => set({ description: v })} /></Field>
        <ImageUpload label="Imagem do evento" value={f.cover_image} onChange={(v) => set({ cover_image: v })} folder="rotta/events" />
      </section>

      <section className="card-rotta p-4 sm:p-7 bg-[#141414] space-y-5">
        <h2 className="font-display tracking-wider uppercase text-sm text-[#C6A762]">SEO</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Meta Title"><Text value={f.seo.meta_title} onChange={(v) => setSeo({ meta_title: v })} /></Field>
          <Field label="OG Image URL"><Text value={f.seo.og_image} onChange={(v) => setSeo({ og_image: v })} /></Field>
          <Field label="Meta Description" cols={2}><Textarea rows={2} value={f.seo.meta_description} onChange={(v) => setSeo({ meta_description: v })} /></Field>
        </div>
      </section>

      <div className="flex justify-end gap-3">
        <Link to="/admin/eventos" className="btn-ghost px-6 py-3 rounded-full text-xs">Cancelar</Link>
        <button disabled={saving} className="btn-gold px-6 py-3 rounded-full text-xs inline-flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
        </button>
      </div>
    </form>
  );
}
